<template>
    <div class="flex flex-col text-gray-800">
        <div v-if="label" :class="[labelClasses, labelClass]">
            {{ label }}
            <span v-if="required"> * </span>
        </div>
        <div class="flex flex-col" :class="optionsWrapperClasses">
            <label
                v-for="(option, index) in options"
                :key="index"
                class="relative flex items-center text-body hover:cursor-pointer"
                :class="[
                    {
                        'justify-between': showArrows || option.appendIcon,
                    },
                    cardOptions
                        ? inputValue ===
                          (typeof option === 'object' ? option.value : option)
                            ? [
                                  cardClasses,
                                  cardSelectedClasses,
                                  'shadow-button-active',
                              ]
                            : cardClasses
                        : '',
                ]"
            >
                <input
                    v-model="inputValue"
                    :checked="
                        inputValue ===
                        (typeof option === 'object' ? option.value : option)
                    "
                    type="radio"
                    class="hidden"
                    :value="typeof option === 'object' ? option.value : option"
                    :name="name"
                    :data-test="`${name}_${option?.value ?? option}`"
                    @input="
                        $emit('initial-selection', {
                            selectedValue: option.value,
                            name: name,
                        })
                    "
                />
                <div class="flex items-center">
                    <div v-if="option.icon">
                        <component
                            :is="option.icon"
                            :selected="
                                inputValue ===
                                (typeof option === 'object'
                                    ? option.value
                                    : option)
                            "
                            class="h-10 w-10 mr-2"
                        />
                    </div>
                    <div
                        v-else
                        class="rounded-full shadow-inner-input flex justify-center items-center mr-2 border border-gray-900 border-opacity-10"
                        :class="inputClasses"
                    >
                        <div
                            v-if="
                                inputValue ===
                                (typeof option === 'object'
                                    ? option.value
                                    : option)
                            "
                            class="rounded-full flex justify-center items-center"
                            :class="[
                                { 'h-4 w-4': lg && !setup },
                                { 'h-4.5 w-4.5': lg && setup },
                                { 'h-1.5 w-1.5': sm },
                                { 'h-2.5 w-2.5': !lg && !sm },
                                selectedOptionClasses,
                            ]"
                        >
                            <CheckIcon
                                class="text-white"
                                :class="[
                                    { 'h-2 w-2': lg },
                                    { 'h-1 w-1': sm },
                                    { 'h-1.5 w-1.5': !lg && !sm },
                                ]"
                            />
                        </div>
                    </div>
                    <span
                        :class="[
                            cardOptions
                                ? [
                                      cardOptionClasses,
                                      {
                                          'text-gray-900':
                                              inputValue ===
                                              (typeof option === 'object'
                                                  ? option.value
                                                  : option),
                                      },
                                  ]
                                : optionClasses,
                            inputValue === option.value && setup
                                ? 'font-semibold'
                                : 'font-normal',
                        ]"
                    >
                        {{ typeof option === "object" ? option.text : option }}
                        <span
                            v-if="option.description"
                            class="text-gray-600 text-label-xs"
                            >{{ option.description }}</span
                        >
                    </span>
                </div>
                <div class="flex items-center gap-x-4">
                    <component
                        :is="option.appendIcon"
                        v-if="option.appendIcon"
                        class="h-6 w-max"
                    />
                    <ChevronRightIcon
                        v-if="showArrows"
                        class="h-6 text-gray-900"
                    />
                </div>
            </label>
        </div>

        <div
            v-if="error || info"
            class="text-subtitle-2"
            :class="{ 'mt-4 text-center': setup }"
        >
            <span v-if="error" class="text-alert">{{ error }}</span>
            <span v-if="info && !error" class="text-gray-700">
                {{ info }}
            </span>
        </div>
    </div>
</template>

<script setup>
import CheckIcon from "@/Components/Icons/CheckIcon.vue";
import ChevronRightIcon from "@/Components/Icons/ChevronRightIcon.vue";
import { fieldProps, useFieldSetup } from "@/Mixins/FormFieldMixin";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";

const props = defineProps({
    ...fieldProps,
    options: {
        type: Array,
        default: () => [],
    },
    cardOptions: {
        type: Boolean,
        default: false,
    },
    showArrows: {
        type: Boolean,
        default: false,
    },
    optionClasses: {
        type: String,
        default: "text-body font-semibold",
    },
    cardClasses: {
        type: String,
        default: "p-4 rounded-lg shadow-button",
    },
    cardSelectedClasses: {
        type: String,
        default: "bg-gray-blueish",
    },
    cardOptionClasses: {
        type: String,
        default: "text-h3 text-gray-900 font-normal flex flex-col",
    },
    selectedOptionClasses: {
        type: String,
        default: "bg-black",
    },
    labelClass: {
        type: String,
        default: "text-label font-semibold mb-2",
    },
    optionsWrapperClass: {
        type: String,
        default: "",
    },
    setup: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(["initial-selection", "update:modelValue"]);

const labelClasses = computed(() => {
    return [{ "text-label-sm": props.sm }, { "text-label-lg": props.lg }];
});

const inputClasses = computed(() => {
    return [
        { "h-6 w-6 min-w-6": props.lg },
        { "h-3 w-3": props.sm },
        { "h-4 w-4": !props.lg && !props.sm },
    ];
});

const optionsWrapperClasses = computed(() =>
    twMerge(
        props.cardOptions ? "gap-y-2" : "gap-y-4",
        props.optionsWrapperClass
    )
);

const { inputValue } = useFieldSetup(props, emit);
</script>
